import React, { useEffect, useState, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import './AdminClubs.css';

interface Club {
  id: number;
  name: string;
  short_name: string;
  slug: string;
  member_count: number;
  is_active: boolean;
  created_at: string | null;
}

interface ClubMember {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status: string;
  joined_at: string | null;
}

interface SearchUser {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

const AdminClubs: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add member search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Edit club
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editShortName, setEditShortName] = useState('');

  useEffect(() => {
    fetchClubs();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setClubs(data.clubs);
      } else {
        setError('Błąd pobierania klubów');
      }
    } catch {
      setError('Błąd sieci');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (club: Club) => {
    setSelectedClub(club);
    setMembersLoading(true);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setEditing(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${club.id}/members`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members);
      }
    } catch {
      // ignore
    } finally {
      setMembersLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/users/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.users);
          setShowDropdown(true);
        }
      } catch {
        // ignore
      }
    }, 300);
  };

  const handleAddMember = async (user: SearchUser) => {
    if (!selectedClub) return;
    setShowDropdown(false);
    setSearchQuery('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${selectedClub.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: user.id })
      });
      if (res.ok) {
        fetchMembers(selectedClub);
        setClubs(prev => prev.map(c => c.id === selectedClub.id ? { ...c, member_count: c.member_count + 1 } : c));
      } else {
        const data = await res.json();
        alert(data.error || 'Błąd dodawania użytkownika');
      }
    } catch {
      alert('Błąd sieci');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!selectedClub) return;
    if (!window.confirm('Czy na pewno chcesz usunąć tego użytkownika z klubu?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${selectedClub.id}/members/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setMembers(prev => prev.filter(m => m.user_id !== userId));
        setClubs(prev => prev.map(c => c.id === selectedClub.id ? { ...c, member_count: Math.max(0, c.member_count - 1) } : c));
      } else {
        alert('Błąd usuwania członka');
      }
    } catch {
      alert('Błąd sieci');
    }
  };

  const handleToggleRole = async (userId: number, currentRole: string) => {
    if (!selectedClub) return;
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${selectedClub.id}/members/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole } : m));
      } else {
        alert('Błąd zmiany roli');
      }
    } catch {
      alert('Błąd sieci');
    }
  };

  const startEditing = () => {
    if (!selectedClub) return;
    setEditName(selectedClub.name);
    setEditShortName(selectedClub.short_name);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedClub) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${selectedClub.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName, short_name: editShortName })
      });
      if (res.ok) {
        const data = await res.json();
        const updated = data.club as Club;
        setSelectedClub(updated);
        setClubs(prev => prev.map(c => c.id === updated.id ? updated : c));
        setEditing(false);
      } else {
        const data = await res.json();
        alert(data.error || 'Błąd zapisu');
      }
    } catch {
      alert('Błąd sieci');
    }
  };

  const handleDeactivate = async () => {
    if (!selectedClub) return;
    if (!window.confirm(`Czy na pewno chcesz dezaktywować klub "${selectedClub.name}"?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${selectedClub.id}/deactivate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        const updated = data.club as Club;
        setSelectedClub(updated);
        setClubs(prev => prev.map(c => c.id === updated.id ? updated : c));
      } else {
        alert('Błąd dezaktywacji');
      }
    } catch {
      alert('Błąd sieci');
    }
  };

  const handleReactivate = async () => {
    if (!selectedClub) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/clubs/${selectedClub.id}/reactivate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        const updated = data.club as Club;
        setSelectedClub(updated);
        setClubs(prev => prev.map(c => c.id === updated.id ? updated : c));
      } else {
        alert('Błąd reaktywacji');
      }
    } catch {
      alert('Błąd sieci');
    }
  };

  if (loading) return <div>Ładowanie klubów...</div>;
  if (error) return <div className="card" style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="card">
      <h2>Zarządzanie klubami</h2>
      <div className="admin-clubs">
        <div className="admin-clubs-sidebar">
          <h3>Kluby ({clubs.length})</h3>
          {clubs.map(club => (
            <div
              key={club.id}
              className={`club-list-item ${selectedClub?.id === club.id ? 'active' : ''} ${!club.is_active ? 'inactive' : ''}`}
              onClick={() => fetchMembers(club)}
            >
              <div>
                <span className="club-name">{club.name}</span>
                {!club.is_active && <span className="club-inactive-badge">nieaktywny</span>}
              </div>
              <span className="club-count">{club.member_count}</span>
            </div>
          ))}
        </div>

        <div className="admin-clubs-main">
          {!selectedClub ? (
            <p className="no-club-selected">Wybierz klub z listy po lewej</p>
          ) : (
            <>
              {/* Club header with edit/deactivate */}
              <div className="club-header">
                {editing ? (
                  <div className="club-edit-form">
                    <div className="club-edit-fields">
                      <label>
                        Nazwa:
                        <input type="text" value={editName} onChange={e => setEditName(e.target.value)} />
                      </label>
                      <label>
                        Skrót (max 10):
                        <input type="text" value={editShortName} onChange={e => setEditShortName(e.target.value)} maxLength={10} />
                      </label>
                    </div>
                    <div className="club-edit-actions">
                      <button className="btn-save" onClick={handleSaveEdit}>Zapisz</button>
                      <button className="btn-cancel" onClick={() => setEditing(false)}>Anuluj</button>
                    </div>
                  </div>
                ) : (
                  <div className="club-header-row">
                    <h3>
                      {selectedClub.name}
                      <span className="club-short-name">({selectedClub.short_name})</span>
                      {!selectedClub.is_active && <span className="status-badge banned">nieaktywny</span>}
                    </h3>
                    <div className="club-header-actions">
                      <button className="btn-edit" onClick={startEditing}>Edytuj</button>
                      {selectedClub.is_active ? (
                        <button className="btn-deactivate" onClick={handleDeactivate}>Dezaktywuj</button>
                      ) : (
                        <button className="btn-reactivate" onClick={handleReactivate}>Reaktywuj</button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Add member search */}
              <div className="add-member-section" ref={dropdownRef}>
                <input
                  type="text"
                  placeholder="Szukaj użytkownika (username, email, imię)..."
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                />
                {showDropdown && searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map(user => (
                      <div
                        key={user.id}
                        className="search-result-item"
                        onClick={() => handleAddMember(user)}
                      >
                        <div className="search-result-name">{user.username}</div>
                        <div className="search-result-email">
                          {user.first_name} {user.last_name} — {user.email}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Members table */}
              {membersLoading ? (
                <div>Ładowanie członków...</div>
              ) : (
                <table className="club-members-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Imię</th>
                      <th>Nazwisko</th>
                      <th>Email</th>
                      <th>Rola</th>
                      <th>Status</th>
                      <th>Dołączył</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Brak członków</td></tr>
                    ) : (
                      members.map(m => (
                        <tr key={m.user_id}>
                          <td>{m.username}</td>
                          <td>{m.first_name}</td>
                          <td>{m.last_name}</td>
                          <td>{m.email}</td>
                          <td><span className={`role-badge ${m.role}`}>{m.role}</span></td>
                          <td><span className={`status-badge ${m.status}`}>{m.status}</span></td>
                          <td>{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '-'}</td>
                          <td className="member-actions">
                            <button
                              className={`btn-role ${m.role === 'admin' ? 'btn-revoke-role' : 'btn-grant-role'}`}
                              onClick={() => handleToggleRole(m.user_id, m.role)}
                            >
                              {m.role === 'admin' ? 'Odbierz admina' : 'Nadaj admina'}
                            </button>
                            <button className="btn-remove" onClick={() => handleRemoveMember(m.user_id)}>
                              Usuń
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClubs;
